import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Ban, MoreVertical, Search, Filter, Download, X, Save, Edit2, Trash2, UserPlus, AlertTriangle, ShieldAlert, Clock, PlayCircle, Wallet, RefreshCw, CheckCircle } from 'lucide-react';
import { usersAPI, authAPI, walletsAPI } from '../utils/api';
import axios from 'axios';
import constant from '../constants/constant';

const UsersManagement = () => {
  const token = sessionStorage.getItem('adminToken');
  const userinfo = React.useMemo(() => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [token]);

  const userType = userinfo?.user_type || 'ADMIN';

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [users, setUsers] = useState([]);
  const [walletsMap, setWalletsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3000);
  };
  
  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    user_type: 'USER',
    country: 'India',
    is_active: true,
    user_id: '',
    diamond_salary: ''
  });

  // Warning Modal
  const [isWarnModalOpen, setIsWarnModalOpen] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [warnReason, setWarnReason] = useState('');

  // Tx Logs Modal
  const [isTxModalOpen, setIsTxModalOpen] = useState(false);
  const [txUser, setTxUser] = useState(null);
  const [txLogs, setTxLogs] = useState([]);
  const [txLoading, setTxLoading] = useState(false);

  const countries = [
    { name: 'India', flag: '🇮🇳' },
    { name: 'USA', flag: '🇺🇸' },
    { name: 'UK', flag: '🇬🇧' },
    { name: 'Canada', flag: '🇨🇦' },
    { name: 'Australia', flag: '🇦🇺' },
    { name: 'UAE', flag: '🇦🇪' },
  ];

  useEffect(() => {
    fetchUsersAndWallets();
  }, []);

  const fetchUsersAndWallets = async () => {
    setLoading(true);
    try {
      const [usersRes, walletsRes] = await Promise.all([
        usersAPI.getUsers(),
        walletsAPI.getWallets()
      ]);
      
      setUsers(usersRes.data || []);
      
      const walletsData = walletsRes.data?.data || [];
      const wMap = {};
      walletsData.forEach(w => {
        wMap[w.user_id] = { balance: w.balance, diamond_balance: w.diamond_balance };
      });
      setWalletsMap(wMap);
      setError(null);
    } catch (err) {
      console.error('Error fetching users and wallets:', err);
      setError('Failed to load user directories. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const openAddModal = () => {
    setEditingUser(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      phone: '',
      user_type: 'USER',
      country: 'India',
      is_active: true,
      user_id: '',
      diamond_salary: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      password: '',
      phone: user.phone || '',
      user_type: user.user_type || 'USER',
      country: 'India',
      is_active: user.is_active ?? true,
      user_id: user.user_id || '',
      diamond_salary: user.diamond_salary || ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { country, ...payload } = formData;
      // Convert diamond_salary to 0 if empty
      if (payload.diamond_salary === '' || payload.diamond_salary === null) {
        payload.diamond_salary = 0;
      }
      if (payload.user_id === '') {
        delete payload.user_id;
      }
      if (payload.phone === '') {
        delete payload.phone;
      }
      if (payload.agency_id === '') {
        payload.agency_id = null;
      }
      
      if (editingUser) {
        if (!payload.password) delete payload.password;
        await usersAPI.updateUser(editingUser.id, payload);
        showToast('User updated successfully');
      } else {
        await usersAPI.createUser(payload);
        showToast('User created successfully');
      }
      setIsModalOpen(false);
      fetchUsersAndWallets();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error saving user', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await usersAPI.deleteUser(id);
        showToast('User deleted successfully');
        fetchUsersAndWallets();
      } catch (err) {
        showToast('Error deleting user', 'error');
      }
    }
  };

  // BAN / UNBAN
  const handleToggleBan = async (user) => {
    const newBanState = user.is_active; // If active, we ban (ban = true). If inactive, we unban (ban = false).
    const actionText = newBanState ? 'ban' : 'unban';
    if (window.confirm(`Are you sure you want to ${actionText} ${user.first_name}?`)) {
      try {
        await axios.put(`${constant.API_BASE_URL}/admin/business/users/${user.id}/ban`, { ban: newBanState }, {
          headers: { Authorization: token }
        });
        showToast(`User ${actionText}ed successfully`);
        fetchUsersAndWallets();
      } catch (err) {
        showToast("Action failed: " + (err.response?.data?.message || err.message), 'error');
      }
    }
  };

  // WARNINGS
  const handleOpenWarnModal = (user) => {
    setTargetUser(user);
    setWarnReason('');
    setIsWarnModalOpen(true);
  };

  const handleSendWarning = async (e) => {
    e.preventDefault();
    if (!targetUser || !warnReason.trim()) return;
    try {
      await axios.post(`${constant.API_BASE_URL}/admin/business/users/${targetUser.id}/warning`, { reason: warnReason.trim() }, {
        headers: { Authorization: token }
      });
      setIsWarnModalOpen(false);
      showToast("Warning recorded successfully.");
    } catch (err) {
      showToast("Failed to record warning: " + (err.response?.data?.message || err.message), 'error');
    }
  };

  // WALLET RESET
  const handleResetWallet = async (user) => {
    if (window.confirm(`WARNING: Are you sure you want to reset ${user.first_name}'s coin wallet to 0.00? This cannot be undone.`)) {
      try {
        await axios.put(`${constant.API_BASE_URL}/admin/business/users/${user.id}/wallet/reset`, {}, {
          headers: { Authorization: token }
        });
        showToast("Wallet reset successfully");
        fetchUsersAndWallets();
      } catch (err) {
        showToast("Wallet reset failed: " + (err.response?.data?.message || err.message), 'error');
      }
    }
  };

  // TRANSACTION LOGS WINDOW
  const handleOpenTxLogs = async (user) => {
    setTxUser(user);
    setTxLogs([]);
    setIsTxModalOpen(true);
    setTxLoading(true);
    try {
      const res = await walletsAPI.getTransactions(user.id);
      setTxLogs(res.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setTxLoading(false);
    }
  };

  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    if (user.user_type === 'BD') return false;
    
    if (statusFilter === 'ACTIVE' && !user.is_active) return false;
    if (statusFilter === 'BANNED' && user.is_active) return false;
    
    const term = searchTerm.toLowerCase();
    const fullName = (user.first_name + ' ' + user.last_name).toLowerCase();
    const userName = (user.user_name || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    const userIdStr = (user.id || '').toString().toLowerCase();
    const userCustomId = (user.user_id || '').toLowerCase();
    
    return fullName.includes(term) || 
           userName.includes(term) || 
           email.includes(term) || 
           userIdStr.includes(term) ||
           userCustomId.includes(term);
  }) : [];

  const handleRefresh = () => {
    setSearchTerm('');
    setStatusFilter('ALL');
    fetchUsersAndWallets();
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">User Management</h2>
          <p className="text-slate-500 mt-1">Audit platform accounts, warning counts, ban statuses, and real-time wallet balances.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handleRefresh}
            className="flex items-center gap-1.5 bg-white text-slate-700 px-4 py-2 text-sm rounded-xl font-semibold shadow-sm border border-gray-200 hover:bg-gray-50 transition active:scale-95 cursor-pointer"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh Accounts
          </button>
          <button
            onClick={openAddModal}
            className="flex items-center gap-1.5 bg-pink-600 text-white px-4 py-2 text-sm rounded-xl font-semibold shadow-lg shadow-pink-500/30 hover:bg-pink-700 transition active:scale-95 cursor-pointer"
          >
            <UserPlus size={16} /> Add New User
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-100/30 border border-slate-100 overflow-hidden">

        {/* Table Toolbar */}
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search user accounts..."
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition font-semibold text-slate-600 text-sm h-10"
            >
              <option value="ALL">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="BANNED">Banned</option>
            </select>
          </div>
          <div className="text-sm font-bold text-slate-500 bg-slate-50 px-4 py-2 rounded-lg border border-slate-100">
            Total Records: <span className="text-pink-600">{filteredUsers.length}</span>
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Loading user accounts...</p>
          </div>
        ) : error ? (
          <div className="p-20 text-center text-red-500">
            {error}
            <button onClick={fetchUsersAndWallets} className="block mx-auto mt-4 text-pink-600 font-bold underline">Try Again</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">User Profile</th>
                  <th className="p-6">Contact Info</th>
                  <th className="p-6">Wallet Balance</th>
                  <th className="p-6">User Type</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50/50 transition duration-150 group">
                    <td className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 font-bold text-lg border-2 border-white shadow-sm shrink-0">
                          {user.first_name?.[0] || 'U'}{user.last_name?.[0] || ''}
                        </div>
                        <div>
                          <p className="font-bold text-slate-800 text-base">{user.first_name} {user.last_name}</p>
                          <p className="text-[10px] text-slate-400">ID: {user.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-6">
                      <p className="text-slate-600 font-semibold">{user.email}</p>
                      <p className="text-xs text-slate-400 mt-1">{user.phone || 'No phone'}</p>
                    </td>
                    <td className="p-6 font-extrabold text-slate-800 text-base">
                      {walletsMap[user.id] ? walletsMap[user.id].balance?.toLocaleString() : '0'} Coins
                      {walletsMap[user.id] && walletsMap[user.id].diamond_balance > 0 && <p className="text-xs text-pink-500 font-bold mt-1">💎 {walletsMap[user.id].diamond_balance?.toLocaleString()} Diamonds</p>}
                      {user.diamond_salary && <p className="text-xs text-blue-500 font-bold mt-1">💰 {user.diamond_salary} Salary</p>}
                    </td>
                    <td className="p-6">
                      <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-bold uppercase">
                        {user.user_type}
                      </span>
                    </td>
                    <td className="p-6">
                      <span className={`
                        inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide
                        ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                      `}>
                        <span className={`w-1.5 h-1.5 rounded-full ${user.is_active ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        {user.is_active ? 'Active' : 'Banned'}
                      </span>
                    </td>
                    <td className="p-6 text-right">
                      <div className="flex justify-end gap-1.5 flex-wrap max-w-[200px] ml-auto">
                        <button 
                          onClick={() => handleOpenTxLogs(user)}
                          className="px-2 py-1 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-md text-[10px] font-bold transition flex items-center gap-1 cursor-pointer"
                          title="View Transaction History"
                        >
                          <Wallet size={12} /> History
                        </button>
                        <button 
                          onClick={() => handleOpenWarnModal(user)}
                          className="px-2 py-1 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 rounded-md text-[10px] font-bold transition border border-yellow-100 flex items-center gap-1 cursor-pointer"
                          title="Issue System Warning"
                        >
                          <AlertTriangle size={12} /> Warn
                        </button>
                        <button 
                          onClick={() => handleToggleBan(user)}
                          className={`px-2 py-1 rounded-md text-[10px] font-bold transition flex items-center gap-1 cursor-pointer ${
                            user.is_active 
                              ? 'bg-red-50 text-red-600 border border-red-100 hover:bg-red-100' 
                              : 'bg-green-50 text-green-600 border border-green-100 hover:bg-green-100'
                          }`}
                        >
                          <Ban size={12} /> {user.is_active ? 'Ban' : 'Unban'}
                        </button>
                        <button 
                          onClick={() => handleResetWallet(user)}
                          className="px-2 py-1 bg-slate-900 text-white hover:bg-slate-800 rounded-md text-[10px] font-bold transition flex items-center gap-1 cursor-pointer"
                          title="Reset Wallet Balance to 0"
                        >
                          <Clock size={12} /> Reset
                        </button>
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-1.5 hover:bg-blue-50 text-gray-400 hover:text-blue-600 rounded-md transition border border-gray-200 hover:border-blue-200 cursor-pointer" title="Edit Profile">
                          <Edit2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan="6" className="p-10 text-center text-gray-500">No users found matching your search.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Edit / Add Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <h3 className="text-xl font-bold text-slate-800">{editingUser ? 'Edit User' : 'Add New User'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50 transition">
                <X size={24} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
              {/* Scrollable Fields Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 uppercase">First Name</label>
                    <input
                      required
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 uppercase">Last Name</label>
                    <input
                      required
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Email Address</label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                  />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-end">
                    <label className="text-xs font-bold text-slate-500 uppercase">Password {editingUser && '(Leave blank to keep same)'}</label>
                    <span className={formData.password.length >= 6 ? "text-[10px] font-bold text-green-500" : "text-[10px] font-bold text-slate-400"}>Min 6 chars</span>
                  </div>
                  <div className="relative">
                    <input
                      required={!editingUser}
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full pl-4 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Phone</label>
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Diamond Salary</label>
                  <input
                    type="number"
                    name="diamond_salary"
                    value={formData.diamond_salary}
                    onChange={handleInputChange}
                    placeholder="e.g. 5000"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Country</label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition font-semibold"
                  >
                    {countries.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.flag} {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">User Role</label>
                  <select
                    required
                    name="user_type"
                    value={formData.user_type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition font-semibold"
                  >
                    <option value="USER">USER</option>
                    <option value="ADMIN">ADMIN</option>
                    <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                    <option value="MANAGER">MANAGER</option>
                    <option value="HOST">HOST</option>
                  </select>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded text-pink-600 focus:ring-pink-500 border-slate-300 cursor-pointer"
                  />
                  <label htmlFor="is_active" className="text-sm font-semibold text-slate-700 cursor-pointer select-none">
                    User is Active
                  </label>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-6 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-8 py-2.5 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 shadow-lg shadow-slate-200 transition active:scale-95"
                  >
                    <Save size={18} /> {editingUser ? 'Save Changes' : 'Create User'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Warning Message Modal */}
      {isWarnModalOpen && targetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <AlertTriangle className="text-yellow-500" size={20} /> Issue Warning
              </h3>
              <button onClick={() => setIsWarnModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSendWarning} className="p-6 space-y-4">
              <p className="text-xs text-slate-500 font-semibold">
                Warning details will be recorded as a Security Alert log linked to {targetUser.first_name}'s account.
              </p>
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-400 uppercase">Warning Reason</label>
                <textarea
                  required
                  rows={3}
                  placeholder="e.g. Terms violation or suspicious login pattern observed."
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition text-sm font-medium"
                  value={warnReason}
                  onChange={(e) => setWarnReason(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsWarnModalOpen(false)}
                  className="px-5 py-2 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl text-xs font-bold active:scale-95 transition"
                >
                  Issue Alert
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transaction Logs Modal */}
      {isTxModalOpen && txUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white">
              <div>
                <h3 className="text-lg font-bold text-slate-800">User Coin Ledger</h3>
                <p className="text-xs text-slate-500">History for: {txUser.first_name} {txUser.last_name} ({txUser.email})</p>
              </div>
              <button onClick={() => setIsTxModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg">
                <X size={24} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50 space-y-4">
              {txLoading ? (
                <div className="p-20 text-center">
                  <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-xs text-slate-500">Loading ledger records...</p>
                </div>
              ) : txLogs.length === 0 ? (
                <div className="p-20 text-center text-slate-400">
                  <Wallet size={36} className="mx-auto mb-2 opacity-30" />
                  <p>No coins activity recorded.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {txLogs.map((tx) => {
                    const isAdd = tx.transaction_type === 'COIN_ADD';
                    return (
                      <div key={tx.id} className="p-4 bg-white rounded-2xl border border-slate-100 flex items-center justify-between shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className={`p-2.5 rounded-xl ${isAdd ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                            {isAdd ? <Clock size={16} /> : <Ban size={16} />}
                          </div>
                          <div>
                            <p className="text-xs font-black uppercase text-slate-400 tracking-wider">{tx.transaction_type}</p>
                            <p className="text-sm font-bold text-slate-800 mt-0.5">{tx.reason || 'No details provided'}</p>
                            <p className="text-[10px] text-slate-400 mt-1">{new Date(tx.created_at || tx.timestamp).toLocaleString()}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-base font-black ${isAdd ? 'text-green-600' : 'text-slate-800'}`}>
                            {isAdd ? '+' : '-'}{Number(tx.amount).toLocaleString()}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">Wallet: {Number(tx.balance_before || 0).toLocaleString()} → {Number(tx.balance_after || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end shrink-0">
              <button 
                onClick={() => setIsTxModalOpen(false)}
                className="px-6 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold"
              >
                Close Ledger
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-6 py-3 rounded-xl shadow-lg border text-sm font-bold flex items-center gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300 ${toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-700 border-green-100'}`}>
          {toast.type === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default UsersManagement;